import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Overtaking questions
export const overtakingMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-ot-1',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-1-dashed-line.png',
    text: {
      en: 'You see a dashed white line between your lane and the lane ahead. What does this mean?',
      nl: 'Je ziet een onderbroken witte lijn tussen je rijstrook en de strook voor je. Wat betekent dit?',
      ar: 'ترى خطا أبيض متقطعا بين ممرك والممر أمامك. ماذا يعني هذا؟'
    },
    options: [
      {
        id: 'q-ot-1o1',
        text: {
          en: 'Overtaking is prohibited',
          nl: 'Inhalen is verboden',
          ar: 'التجاوز ممنوع'
        }
      },
      {
        id: 'q-ot-1o2',
        text: {
          en: 'Overtaking is permitted when safe and the road ahead is clear',
          nl: 'Inhalen is toegestaan wanneer het veilig is en de weg voor je leeg is',
          ar: 'يسمح بالتجاوز عندما يكون آمنا والطريق أمامك خالية'
        }
      },
      {
        id: 'q-ot-1o3',
        text: {
          en: 'You must overtake immediately',
          nl: 'Je moet onmiddellijk inhalen',
          ar: 'يجب عليك التجاوز فورا'
        }
      }
    ],
    correctAnswerId: 'q-ot-1o2',
    explanation: {
      en: 'A dashed white line (broken line) allows overtaking when it is safe to do so. You must still check for oncoming traffic and ensure the road ahead is clear for a safe distance. (RVV 1990 art. 40)',
      nl: 'Een onderbroken witte lijn staat inhalen toe wanneer het veilig is. Je moet nog steeds op tegengesteld verkeer controleren en ervoor zorgen dat de weg voor je een veilige afstand vrij is. (RVV 1990 art. 40)',
      ar: 'يسمح خط أبيض متقطع بالتجاوز عندما يكون آمنا. يجب عليك التحقق من حركة المرور القادمة والتأكد من أن الطريق أمامك خالية لمسافة آمنة. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-2',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-2-solid-line.png',
    text: {
      en: 'You see a solid white line between your lane and the lane ahead. What does this prohibit?',
      nl: 'Je ziet een ononderbroken witte lijn tussen je rijstrook en de strook voor je. Wat verbiedt dit?',
      ar: 'ترى خطا أبيض متصلا بين ممرك والممر أمامك. ماذا يحظر؟'
    },
    options: [
      {
        id: 'q-ot-2o1',
        text: {
          en: 'You may overtake if the road is clear',
          nl: 'Je mag inhalen als de weg leeg is',
          ar: 'يمكنك التجاوز إذا كان الطريق خاليا'
        }
      },
      {
        id: 'q-ot-2o2',
        text: {
          en: 'Overtaking is prohibited; do not cross this line',
          nl: 'Inhalen is verboden; steek deze lijn niet over',
          ar: 'التجاوز ممنوع؛ لا تعبر هذا الخط'
        }
      },
      {
        id: 'q-ot-2o3',
        text: {
          en: 'You must overtake before the line ends',
          nl: 'Je moet inhalen voordat de lijn eindigt',
          ar: 'يجب عليك التجاوز قبل انتهاء الخط'
        }
      }
    ],
    correctAnswerId: 'q-ot-2o2',
    explanation: {
      en: 'A solid white line prohibits crossing. You must not overtake when a solid white line is on your side of the road. It means the road layout or visibility does not permit safe overtaking. (RVV 1990 art. 40)',
      nl: 'Een ononderbroken witte lijn verbiedt het oversteken. Je mag niet inhalen wanneer een ononderbroken witte lijn aan je zijde van de weg staat. Het betekent dat de wegindeling of zichtbaarheid veilig inhalen niet toestaat. (RVV 1990 art. 40)',
      ar: 'يحظر خط أبيض متصل العبور. يجب عليك عدم التجاوز عند وجود خط أبيض متصل على جانبك من الطريق. هذا يعني أن تخطيط الطريق أو الرؤية لا تسمح بالتجاوز الآمن. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-3',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-3-300m-sight.png',
    text: {
      en: 'Before overtaking, how far ahead must you be able to see clearly?',
      nl: 'Voordat je inhaalt, hoe ver vooruit moet je duidelijk kunnen zien?',
      ar: 'قبل التجاوز، إلى أي مسافة أمامك يجب أن تكون قادرا على الرؤية بوضوح؟'
    },
    options: [
      {
        id: 'q-ot-3o1',
        text: {
          en: '100 metres',
          nl: '100 meter',
          ar: '100 متر'
        }
      },
      {
        id: 'q-ot-3o2',
        text: {
          en: '300 metres minimum',
          nl: '300 meter minimaal',
          ar: '300 متر بحد أدنى'
        }
      },
      {
        id: 'q-ot-3o3',
        text: {
          en: 'Only 50 metres is needed',
          nl: 'Slechts 50 meter is nodig',
          ar: '50 متر فقط مطلوب'
        }
      }
    ],
    correctAnswerId: 'q-ot-3o2',
    explanation: {
      en: 'You must be able to see at least 300 metres ahead and be certain the oncoming lane is clear for the entire overtaking manoeuvre. Never overtake if you cannot see far enough ahead to be safe. (RVV 1990 art. 40)',
      nl: 'Je moet minstens 300 meter vooruit kunnen zien en zeker zijn dat de tegemoetkomende strook voor de hele inhaalmanouvre leeg is. Haal nooit in als je niet ver genoeg vooruit kunt zien om veilig te zijn. (RVV 1990 art. 40)',
      ar: 'يجب أن تكون قادرا على رؤية ما لا يقل عن 300 متر إلى الأمام وأن تكون متأكدا من أن ممر المقابل خال لمناورة التجاوز بأكملها. لا تتجاوز أبدا إذا لم تكن قادرا على الرؤية بعيدا بما يكفي لتكون آمنا. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-4',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-4-night-driving.png',
    text: {
      en: 'Why is overtaking more dangerous at night?',
      nl: 'Waarom is inhalen \'s nachts gevaarlijker?',
      ar: 'لماذا يكون التجاوز أكثر خطورة في الليل؟'
    },
    options: [
      {
        id: 'q-ot-4o1',
        text: {
          en: 'Oncoming vehicles are harder to see; you cannot see as far ahead with headlights',
          nl: 'Tegemoetkomende voertuigen zijn moeilijker te zien; je kunt niet zo ver vooruit zien met koplampen',
          ar: 'يصعب رؤية المركبات القادمة؛ لا يمكنك الرؤية بعيدا أمامك بالمصابيح الأمامية'
        }
      },
      {
        id: 'q-ot-4o2',
        text: {
          en: 'Night driving is not more dangerous',
          nl: 'Nachtrijden is niet gevaarlijker',
          ar: 'القيادة الليلية ليست أكثر خطورة'
        }
      },
      {
        id: 'q-ot-4o3',
        text: {
          en: 'Other drivers always use main beam at night',
          nl: 'Andere bestuurders gebruiken \'s nachts altijd lang licht',
          ar: 'السائقون الآخرون يستخدمون دائما شعاع المصابيح الرئيسية في الليل'
        }
      }
    ],
    correctAnswerId: 'q-ot-4o1',
    explanation: {
      en: 'At night, visibility is severely limited. Oncoming vehicles are hard to spot, and your headlights only show a limited distance ahead. Do not overtake unless you can see clearly that the road ahead is completely free. (RVV 1990 art. 40)',
      nl: 'In de nacht is het zicht ernstig beperkt. Tegemoetkomend verkeer is moeilijk op te spotten, en je koplampen tonen slechts een beperkte afstand vooruit. Haal niet in tenzij je duidelijk kunt zien dat de weg vooruit volkomen vrij is. (RVV 1990 art. 40)',
      ar: 'في الليل، تكون الرؤية مقيدة بشدة. من الصعب اكتشاف المركبات القادمة، ومصابيحك الأمامية تظهر فقط مسافة محدودة إلى الأمام. لا تتجاوز إلا إذا كنت ترى بوضوح أن الطريق أمامك خال تماما. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-5',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-5-oncoming-traffic.png',
    text: {
      en: 'Before you overtake, what must you check regarding oncoming traffic?',
      nl: 'Voordat je inhaalt, wat moet je controleren met betrekking tot tegengesteld verkeer?',
      ar: 'قبل أن تتجاوز، ماذا يجب عليك التحقق منه فيما يتعلق بحركة المرور القادمة؟'
    },
    options: [
      {
        id: 'q-ot-5o1',
        text: {
          en: 'Check mirrors and be certain no oncoming vehicle is approaching in the lane you plan to use',
          nl: 'Controleer spiegels en wees zeker dat geen tegengesteld voertuig nadert in de strook die je wilt gebruiken',
          ar: 'تحقق من المرايا وتأكد من عدم اقتراب أي مركبة قادمة في الممر الذي تريد استخدامه'
        }
      },
      {
        id: 'q-ot-5o2',
        text: {
          en: 'Oncoming traffic will move aside',
          nl: 'Tegengesteld verkeer zal opzij gaan',
          ar: 'ستتحرك حركة المرور القادمة جانبا'
        }
      },
      {
        id: 'q-ot-5o3',
        text: {
          en: 'No need to check; flash your lights to warn them',
          nl: 'Geen controle nodig; knipperen met je lichten om hen te waarschuwen',
          ar: 'لا حاجة للتحقق؛ اومض بأضواءك لتحذيرهم'
        }
      }
    ],
    correctAnswerId: 'q-ot-5o1',
    explanation: {
      en: 'Before overtaking, you must check your mirrors and look ahead to be absolutely certain no oncoming vehicle is approaching. You must have enough clear road to complete the entire overtaking manoeuvre safely. (RVV 1990 art. 40)',
      nl: 'Voordat je inhaalt, moet je je spiegels controleren en vooruit kijken om zeker te zijn dat geen tegengesteld voertuig nadert. Je moet genoeg vrije weg hebben om de hele inhaalmanouvre veilig af te ronden. (RVV 1990 art. 40)',
      ar: 'قبل التجاوز، يجب عليك التحقق من مرآتك والنظر إلى الأمام للتأكد من عدم اقتراب أي مركبة قادمة. يجب أن يكون لديك طريق خال كاف لإكمال مناورة التجاوز بأكملها بأمان. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-6',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-6-left-turn-sign.jpg',
    text: {
      en: 'You see a left turn sign ahead. Is it safe to overtake?',
      nl: 'Je ziet een linksafslagbord voor je. Is het veilig om in te halen?',
      ar: 'ترى علامة انعطاف يسار أمامك. هل من الآمن التجاوز؟'
    },
    options: [
      {
        id: 'q-ot-6o1',
        text: {
          en: 'Yes, as long as you are fast',
          nl: 'Ja, zolang je snel bent',
          ar: 'نعم، طالما أنك سريع'
        }
      },
      {
        id: 'q-ot-6o2',
        text: {
          en: 'No; do not overtake where there is a junction or turning ahead',
          nl: 'Nee; haal niet in waar een kruising of afslag voor je ligt',
          ar: 'لا؛ لا تتجاوز حيث توجد تقاطع أو انعطاف أمامك'
        }
      },
      {
        id: 'q-ot-6o3',
        text: {
          en: 'Yes, if no oncoming traffic is visible',
          nl: 'Ja, als geen tegengesteld verkeer zichtbaar is',
          ar: 'نعم، إذا لم تكن هناك حركة مرور قادمة مرئية'
        }
      }
    ],
    correctAnswerId: 'q-ot-6o2',
    explanation: {
      en: 'Do not overtake near junctions, turnings, or road signs indicating a junction ahead. A vehicle may turn into your path unexpectedly. Wait until you have passed the junction before overtaking. (RVV 1990 art. 40)',
      nl: 'Haal niet in nabij kruisingen, afslagen of wegborden die een kruising aangeven. Een voertuig kan onverwacht in je weg draaien. Wacht tot je de kruising bent gepasseerd voordat je inhaalt. (RVV 1990 art. 40)',
      ar: 'لا تتجاوز بالقرب من التقاطعات أو الانعطافات أو علامات الطريق التي تشير إلى تقاطع أمامك. قد تنعطف مركبة بشكل غير متوقع في طريقك. انتظر حتى تمر التقاطع قبل التجاوز. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-7',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-7-bus-halte.png',
    text: {
      en: 'You are approaching a bus stop. Is it safe to overtake the vehicle in front?',
      nl: 'Je nadert een bushalte. Is het veilig om het voertuig voor je in te halen?',
      ar: 'أنت تقترب من محطة حافلة. هل من الآمن تجاوز المركبة أمامك؟'
    },
    options: [
      {
        id: 'q-ot-7o1',
        text: {
          en: 'Yes, quickly before the bus moves',
          nl: 'Ja, snel voordat de bus beweegt',
          ar: 'نعم، بسرعة قبل أن تتحرك الحافلة'
        }
      },
      {
        id: 'q-ot-7o2',
        text: {
          en: 'No; do not overtake near bus stops — passengers may cross the road',
          nl: 'Nee; haal niet in nabij bushaltes — passagiers kunnen de weg oversteken',
          ar: 'لا؛ لا تتجاوز بالقرب من محطات الحافلات — قد يعبر الركاب الطريق'
        }
      },
      {
        id: 'q-ot-7o3',
        text: {
          en: 'Only if the bus is not moving',
          nl: 'Alleen als de bus niet beweegt',
          ar: 'فقط إذا لم تكن الحافلة تتحرك'
        }
      }
    ],
    correctAnswerId: 'q-ot-7o2',
    explanation: {
      en: 'Do not overtake near bus stops. Passengers are crossing or about to cross the road, and visibility may be blocked. Wait until you have passed the stop and the road is clear. (RVV 1990 art. 40)',
      nl: 'Haal niet in nabij bushaltes. Passagiers zijn bezig met of op het punt om de weg over te steken, en het zicht kan geblokkeerd zijn. Wacht tot je de halte bent gepasseerd en de weg leeg is. (RVV 1990 art. 40)',
      ar: 'لا تتجاوز بالقرب من محطات الحافلات. الركاب يعبرون أو على وشك عبور الطريق، والرؤية قد تكون مسدودة. انتظر حتى تمر المحطة والطريق خالية. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-8',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-8-safe-spacing.png',
    text: {
      en: 'After completing an overtake, how much space must you have before returning to your original lane?',
      nl: 'Nadat je bent ingehaald, hoeveel ruimte moet je hebben voordat je naar je oorspronkelijke strook terugkeert?',
      ar: 'بعد الانتهاء من التجاوز، كم مساحة يجب أن تكون لديك قبل العودة إلى ممرك الأصلي؟'
    },
    options: [
      {
        id: 'q-ot-8o1',
        text: {
          en: 'Any gap is acceptable',
          nl: 'Elke kloof is aanvaardbaar',
          ar: 'أي فجوة مقبولة'
        }
      },
      {
        id: 'q-ot-8o2',
        text: {
          en: 'You must have passed the vehicle completely and have a safe distance between you',
          nl: 'Je moet het voertuig volledig zijn gepasseerd en een veilige afstand tussen je hebben',
          ar: 'يجب أن تكون قد تجاوزت المركبة تماما وأن يكون لديك مسافة آمنة بينك'
        }
      },
      {
        id: 'q-ot-8o3',
        text: {
          en: 'You can cut back as soon as your car is ahead',
          nl: 'Je kunt teruggaan zodra je auto vooruit is',
          ar: 'يمكنك العودة بمجرد أن تكون سيارتك أمامك'
        }
      }
    ],
    correctAnswerId: 'q-ot-8o2',
    explanation: {
      en: 'You must completely pass the vehicle and ensure there is a safe distance between you before returning to your lane. Cutting back too early can clip the rear of the vehicle or force the driver to brake. (RVV 1990 art. 40)',
      nl: 'Je moet het voertuig volledig passeren en een veilige afstand tussen je en het voertuig garanderen voordat je naar je strook terugkeert. Te vroeg teruggaan kan de achterkant van het voertuig raken of de bestuurder dwingen te remmen. (RVV 1990 art. 40)',
      ar: 'يجب أن تتجاوز المركبة تماما وتضمن وجود مسافة آمنة بينك قبل العودة إلى ممرك. العودة مبكرا جدا قد يؤدي إلى اقتطاع مؤخرة المركبة أو إجبار السائق على الفرملة. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-9',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-9-prohibited-scenarios.jpg',
    text: {
      en: 'In which situations must you never overtake?',
      nl: 'In welke situaties mag je nooit inhalen?',
      ar: 'في أي الحالات يجب أن لا تتجاوز أبدا؟'
    },
    options: [
      {
        id: 'q-ot-9o1',
        text: {
          en: 'At zebra crossings, level crossings, junctions, and when visibility is poor',
          nl: 'Bij zebrapadden, spoorwegovergangen, kruisingen en wanneer het zicht slecht is',
          ar: 'عند معابر المشاة والمستويات والتقاطعات وعندما تكون الرؤية سيئة'
        }
      },
      {
        id: 'q-ot-9o2',
        text: {
          en: 'Overtaking is always allowed if you signal',
          nl: 'Inhalen is altijd toegestaan als je signaleert',
          ar: 'يسمح بالتجاوز دائما إذا أشرت'
        }
      },
      {
        id: 'q-ot-9o3',
        text: {
          en: 'Only during rush hour',
          nl: 'Alleen tijdens spitsuur',
          ar: 'فقط أثناء ساعات الذروة'
        }
      }
    ],
    correctAnswerId: 'q-ot-9o1',
    explanation: {
      en: 'Never overtake: (1) at or near zebra crossings or pedestrian crossings, (2) at or near level crossings, (3) at or near junctions or turnings, (4) when visibility is reduced (fog, rain, night), (5) where there is a solid line on your side. (RVV 1990 art. 40)',
      nl: 'Haal nooit in: (1) op of nabij zebrapadden of voetgangersoversteekplaatsen, (2) op of nabij spoorwegovergangen, (3) op of nabij kruisingen of afslagen, (4) wanneer het zicht beperkt is (mist, regen, nacht), (5) waar een ononderbroken lijn aan je zijde ligt. (RVV 1990 art. 40)',
      ar: 'لا تتجاوز أبدا: (1) عند أو بالقرب من معابر المشاة، (2) عند أو بالقرب من معابر السكك الحديدية، (3) عند أو بالقرب من التقاطعات أو الانعطافات، (4) عندما تكون الرؤية مقيدة (ضباب، مطر، ليل)، (5) حيث يوجد خط متصل على جانبك. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  },
  {
    id: 'q-ot-10',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/overtaking/overtaking-10-safe-procedure.jpg',
    text: {
      en: 'What is the correct overtaking procedure?',
      nl: 'Wat is de juiste inhalprocedure?',
      ar: 'ما هو الإجراء الصحيح للتجاوز؟'
    },
    options: [
      {
        id: 'q-ot-10o1',
        text: {
          en: 'Check mirrors and mirrors, signal, move out, pass, signal, return to lane',
          nl: 'Controleer spiegels, signaleer, rij uit, haal in, signaleer, keer terug naar strook',
          ar: 'تحقق من المرايا، أشر، تحرك للخارج، تجاوز، أشر، عد إلى الممر'
        }
      },
      {
        id: 'q-ot-10o2',
        text: {
          en: 'Just move out and overtake',
          nl: 'Rijd gewoon uit en haal in',
          ar: 'فقط تحرك للخارج والتجاوز'
        }
      },
      {
        id: 'q-ot-10o3',
        text: {
          en: 'Use the horn and overtake immediately',
          nl: 'Gebruik de hoorn en haal onmiddellijk in',
          ar: 'استخدم البوق وتجاوز فورا'
        }
      }
    ],
    correctAnswerId: 'q-ot-10o1',
    explanation: {
      en: 'The correct procedure is: (1) Check mirrors to see traffic behind and ahead, (2) Check it is legal to overtake (no solid line), (3) Signal your intention to move left, (4) Move out smoothly, (5) Overtake with a safe distance, (6) Signal to return right, (7) Return to your lane when safe. (RVV 1990 art. 40)',
      nl: 'De juiste procedure is: (1) Controleer spiegels op verkeer voor en achter, (2) Controleer dat inhalen wettelijk is (geen ononderbroken lijn), (3) Signaleer je voornemen naar links te gaan, (4) Rijd voorzichtig uit, (5) Haal in met veilige afstand, (6) Signaleer terug naar rechts, (7) Keer naar je strook terug wanneer veilig. (RVV 1990 art. 40)',
      ar: 'الإجراء الصحيح هو: (1) تحقق من المرايا لرؤية حركة المرور أمامك وخلفك، (2) تحقق من أن التجاوز قانوني (لا يوجد خط متصل)، (3) أشر لنيتك في التحرك لليسار، (4) تحرك بسلاسة، (5) تجاوز بمسافة آمنة، (6) أشر للعودة لليمين، (7) عد إلى ممرك عندما يكون آمنا. (RVV 1990 art. 40)'
    },
    subject: 'Overtaking'
  }
];
